import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { IMoveBoardItem, IOpportunity } from '../../../../extViewmodels/sdk/models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { openUrlInNewTab } from '../../../../models/UiUtils';
import {
	invalidateArchivedOpportunities,
	useArchivedOpportunities,
} from '../../../../queries/Boards/useArchivedOpportunities';
import { useArchiveOpportunity } from '../../../../queries/Boards/useArchiveOpportunity';
import { invalidateAllBoardStageItems } from '../../../../queries/Boards/useBoardStageItems';
import { invalidateAllBoardStageItemsSearch } from '../../../../queries/Boards/useBoardStageItemsSearch';
import { invalidateAllBoardStageItemsTotals } from '../../../../queries/Boards/useBoardStageItemsTotals';
import { useDeleteOpportunitiesByFilter } from '../../../../queries/Boards/useDeleteOpportunitiesByFilter';
import { useDeleteOpportunity } from '../../../../queries/Boards/useDeleteOpportunity';
import { invalidateAllGetBoardItems } from '../../../../queries/Boards/useGetBoardItems';
import { useRestoreArchivedOpportunity } from '../../../../queries/Boards/useRestoreArchivedOpportunity';
import { Button as ButtonV2 } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Pill } from '../../../components-v2/Pill/Pill';
import { Button } from '../../../components/Button';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { AutomationIcon } from '../../../components/svgs/icons/AutomationIcon';
import { SimpleTooltip } from '../../../components/Tooltip';
import { PlusIcon } from '../../../components/svgs/icons/PlusIcon';
import { useOpportunitiesContext } from '../../../containers/dataBoards/OpportunitiesBoard/context';
import { IStageAutomation } from '../../../containers/dataBoards/OpportunitiesBoard/OpportunitiesContent';
import { baseStyleSheet } from '../../../styles/styles';
import {
	KanbanBoard,
	KanbanBoardCard,
	KanbanBoardCardContent,
	KanbanBoardCardTitle,
	KanbanBoardColumn,
	KanbanBoardColumnFooter,
	KanbanBoardColumnFooterContent,
	KanbanBoardColumnHeader,
	KanbanBoardColumnTitle,
} from '../../KanbanBoard';
import { styleSheet } from './styles';

interface OpportunityKanbanBoardProps {
	onAddCardClicked: (stage: Api.IBoardStage, force: boolean) => void;
	onCardDrop: (opportunity: Api.IOpportunity, moveBoardItem: IMoveBoardItem) => Promise<void>;
	onColumnDrop: (sourceIndex: number, targetIndex: number) => void;
	stagesWithAutomations?: IStageAutomation[];
}

const ARCHIVED_STAGE_ID = 'archived';

const formatCurrency = (value: number) => {
	return value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

const StageAutomationLink: React.FC<{
	boardName: string;
	stageName: string;
	stagesWithAutomations: IStageAutomation[];
}> = ({ boardName, stageName, stagesWithAutomations }) => {
	const history = useHistory();
	const tagName = `${boardName}: ${stageName}`;
	const automation = stagesWithAutomations.find(x => x.name === tagName && !!x.automationTemplateId);

	if (!automation) return null;

	return (
		<SimpleTooltip title={`Automation: ${automation.automationName || tagName}`}>
			<ButtonV2
				type='button'
				variant='ghost'
				size='icon'
				aria-label={`View automation ${automation.automationName || tagName}`}
				styles={[styleSheet.automationIconButton]}
				onClick={e => {
					e.stopPropagation();
					history.push(`/automations/${automation.automationTemplateId}/canvas`);
				}}
			>
				<AutomationIcon />
			</ButtonV2>
		</SimpleTooltip>
	);
};

export const OpportunityKanbanBoard: React.FC<OpportunityKanbanBoardProps> = ({
	onAddCardClicked,
	onCardDrop,
	onColumnDrop,
	stagesWithAutomations = [],
}) => {
	const { board, opportunities, isColumnDraggingEnabled, showArchived, selectedStage, isSearching, searchRequest } =
		useOpportunitiesContext();
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('OpportunityKanbanBoard');
	const [localOpportunities, setLocalOpportunities] = React.useState<IOpportunity[]>(opportunities ?? []);

	const archivedOpportunitiesQuery = useArchivedOpportunities({
		enabled: showArchived && !!board?.id,
		boardId: board?.id,
		refetchInterval: 5000,
	});
	const archivedOpportunities = React.useMemo(
		() => archivedOpportunitiesQuery.data?.pages.flatMap(page => page.values) ?? [],
		[archivedOpportunitiesQuery.data]
	);
	const restoreMutation = useRestoreArchivedOpportunity({
		onSuccess: () => {
			invalidateArchivedOpportunities();
			invalidateAllBoardStageItems();
		},
	});

	React.useEffect(() => {
		const tempSortedOpportunities = [...(opportunities ?? []), ...archivedOpportunities];
		const sortedOpportunities = tempSortedOpportunities.sort((a, b) => {
			const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
			const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
			return dateB - dateA;
		});
		setLocalOpportunities(sortedOpportunities);
	}, [opportunities, archivedOpportunities]);

	const handleCardDrop = async (opportunity: Api.IOpportunity, moveBoardItem: IMoveBoardItem) => {
		await onCardDrop(opportunity, moveBoardItem);

		invalidateAllBoardStageItems();
	};

	const columns = React.useMemo(() => {
		let tempStages = board?.stages.map(stage => ({
			stage,
			opportunities: localOpportunities
				.filter(opp => opp.boardStage?.id === stage.id && !opp.isArchived)
				.sort((a, b) => a.position - b.position),
		}));
		if (showArchived) {
			tempStages.push({
				stage: { id: ARCHIVED_STAGE_ID, name: 'Archived' },
				opportunities: localOpportunities.filter(opp => opp.isArchived),
			});
		}
		if (selectedStage) {
			tempStages = tempStages.filter(column => column.stage.id === selectedStage.id);
		}

		return tempStages;
	}, [board?.stages, localOpportunities, showArchived, selectedStage]);

	const calculateColumnTotal = (columnOpportunities: IOpportunity[]) => {
		return columnOpportunities.reduce((sum, opp) => sum + (opp.dealSize || 0), 0);
	};

	const handleColumnDrop = (sourceIndex: number, targetIndex: number) => {
		onColumnDrop(sourceIndex, targetIndex);
	};

	const onEntityClicked = (contactId: string, e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
		const path = `${window.location.origin}/#/people/${contactId}`;
		if (e.ctrlKey || e.metaKey) {
			openUrlInNewTab(path);
			e.preventDefault();
		} else {
			window.location.href = path;
		}
	};
	const { setOpportunityToEdit, setOpportunityToMove } = useOpportunitiesContext();

	const archiveMutation = useArchiveOpportunity({
		onSuccess: () => {
			invalidateAllGetBoardItems();
			invalidateArchivedOpportunities();
		},
	});

	const deleteMutation = useDeleteOpportunity({
		onSuccess: () => invalidateAllGetBoardItems(),
	});

	const [stageToClear, setStageToClear] = React.useState<Api.IBoardStage>(null);

	const clearStageMutation = useDeleteOpportunitiesByFilter({
		onSuccess: deletedCount => {
			setStageToClear(null);
			invalidateAllBoardStageItems();
			invalidateAllBoardStageItemsSearch();
			invalidateAllBoardStageItemsTotals();
			invalidateAllGetBoardItems();
			toast.success(`${deletedCount} ${deletedCount === 1 ? 'opportunity' : 'opportunities'} deleted`);
		},
		onError: error => {
			setStageToClear(null);
			logApiError('DeleteOpportunitiesByFilter-Error', error);
			toast.error(error?.systemMessage ?? 'Failed to delete opportunities');
		},
	});

	const onClearStageFinished = (shouldDelete: boolean) => {
		if (!shouldDelete || !stageToClear) {
			setStageToClear(null);
			return;
		}

		clearStageMutation.mutate({
			boardId: board.id,
			stageId: stageToClear.id,
			filter: isSearching ? searchRequest : undefined,
		});
	};

	const handleClickToArchive = (opportunity: Api.IOpportunity) => {
		archiveMutation.mutate({ itemId: opportunity.id });
	};

	const handleClickToRestore = (opportunity: Api.IOpportunity) => {
		restoreMutation.mutate({ itemId: opportunity.id, stageId: opportunity.boardStage?.id });
	};

	const handleClickToDelete = (opportunity: Api.IOpportunity) => {
		deleteMutation.mutate({ itemId: opportunity.id });
	};

	const handleClickToEdit = (opportunity: Api.IOpportunity) => {
		if (opportunity.isArchived) {
			handleClickToRestore(opportunity);
		} else {
			setOpportunityToEdit(opportunity);
		}
	};

	const handleClickToMove = (opportunity: Api.IOpportunity) => {
		setOpportunityToMove(opportunity);
	};

	return (
		<KanbanBoard>
			{columns.map((column, columnIndex) => (
				<KanbanBoardColumn
					key={column.stage.id}
					id={column.stage.id}
					index={columnIndex}
					enableDragging={isColumnDraggingEnabled}
					onColumnDrop={handleColumnDrop}
					data={column}
				>
					<KanbanBoardColumnHeader>
						<KanbanBoardColumnTitle>
							{column.stage.name}
							{board?.name && (
								<StageAutomationLink
									boardName={board.name}
									stageName={column.stage.name}
									stagesWithAutomations={stagesWithAutomations}
								/>
							)}
						</KanbanBoardColumnTitle>
						<div className={css(styleSheet.columnHeaderActions)}>
							<Button
								kind='reverse'
								size='small'
								className={css(styleSheet.addCardButton)}
								onClick={() => onAddCardClicked(column.stage, false)}
							>
								<PlusIcon /> Add
							</Button>
							{column.stage.id !== ARCHIVED_STAGE_ID ? (
								<Dropdown>
									<DropdownEllipsisTrigger />
									<DropdownContent align='end' styles={[baseStyleSheet.w56]}>
										<DropdownItem onClick={() => setStageToClear(column.stage)}>Delete all cards</DropdownItem>
									</DropdownContent>
								</Dropdown>
							) : null}
						</div>
					</KanbanBoardColumnHeader>

					{column.opportunities.map((opportunity, cardIndex) => {
						const owner = opportunity.assignees[0];
						const contactVm = new Api.ContactViewModel(userSession, opportunity.primaryContact);

						return (
							<KanbanBoardCard
								key={opportunity.id}
								id={opportunity.id}
								index={cardIndex}
								onCardDrop={handleCardDrop}
								data={opportunity}
							>
								<KanbanBoardCardContent>
									<KanbanBoardCardTitle
										name={opportunity.name}
										link={`/dataBoards/opportunities/${board.id}/${opportunity.id}/`}
									>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												{!opportunity.isArchived ? (
													<DropdownItem onClick={() => handleClickToArchive(opportunity)}>Archive</DropdownItem>
												) : null}
												{handleClickToMove ? (
													<DropdownItem onClick={() => handleClickToMove(opportunity)}>Move to Column</DropdownItem>
												) : null}
												{handleClickToEdit ? (
													<DropdownItem onClick={() => handleClickToEdit(opportunity)}>
														{opportunity.isArchived ? 'Restore' : 'Edit'}
													</DropdownItem>
												) : null}
												<DropdownItem onClick={() => handleClickToDelete(opportunity)}>Permanently Delete</DropdownItem>
											</DropdownContent>
										</Dropdown>
									</KanbanBoardCardTitle>

									<div className={css(styleSheet.cardDetails)}>
										<div className={css(styleSheet.personDetails)}>
											<label className={css(styleSheet.personDetailsLabel)}>Contact:</label>
											{contactVm ? (
												<a
													href={`#/people/${opportunity.primaryContact.id}`}
													className={css(styleSheet.contactName, baseStyleSheet.truncateText)}
													onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
														onEntityClicked(opportunity.primaryContact.id, e)
													}
												>
													{getDisplayName(opportunity.primaryContact)}
												</a>
											) : (
												'No Contact'
											)}
										</div>
										<div className={css(styleSheet.personDetails)}>
											<label className={css(styleSheet.personDetailsLabel)}>Owner:</label>
											<span className={css(styleSheet.personDetailsValue, baseStyleSheet.truncateText)}>
												{getDisplayName(owner) || 'No owner'}
											</span>
										</div>

										<footer className={css(styleSheet.footer)}>
											<div className={css(styleSheet.closeDate)}>
												Close date: {opportunity.closeDate ? new Date(opportunity.closeDate).toLocaleDateString() : '-'}
											</div>

											{opportunity.dealSize ? (
												<div className={css(styleSheet.dealSize)}>{formatCurrency(opportunity.dealSize)}</div>
											) : null}
										</footer>

										{(() => {
											const displayTags =
												opportunity.tagsCollection && opportunity.tagsCollection.length > 0
													? opportunity.tagsCollection
													: opportunity.tags?.map(tagName => ({
															name: tagName,
															id: tagName,
															color: undefined as Api.IAccountTag['color'],
														})) || [];

											if (displayTags.length > 0) {
												return (
													<div className={css(styleSheet.cardTags)}>
														{displayTags.map(tag => (
															<Pill
																key={'id' in tag ? tag.id : tag.name}
																variant='tag'
																label={tag.name}
																color={'color' in tag ? tag.color : undefined}
															/>
														))}
													</div>
												);
											}
											return null;
										})()}
									</div>
								</KanbanBoardCardContent>
							</KanbanBoardCard>
						);
					})}

					<KanbanBoardColumnFooter>
						<KanbanBoardColumnFooterContent>
							<div className={css(styleSheet.columnFooter)}>
								<div>
									<label>Amount:</label>
									<span className={css(styleSheet.dealSize)}>
										{formatCurrency(calculateColumnTotal(column.opportunities))}
									</span>
								</div>
								<div>
									<label>Showing:</label>
									<span className={css(styleSheet.totalValue)}>{column.opportunities.length}</span>
								</div>
							</div>
						</KanbanBoardColumnFooterContent>
					</KanbanBoardColumnFooter>
				</KanbanBoardColumn>
			))}
			<DeleteConfirmation
				isOpen={!!stageToClear}
				onFinish={onClearStageFinished}
				title={stageToClear ? `Delete all cards in ${stageToClear.name}?` : 'Delete all cards?'}
				destructiveButtonText='Delete all'
				showSpinner={clearStageMutation.isPending}
				bodyText={
					isSearching
						? 'This permanently deletes the cards currently shown in this column for your search. Archived opportunities are not affected. This cannot be undone.'
						: 'This permanently deletes every opportunity in this column. Archived opportunities are not affected. This cannot be undone.'
				}
			/>
		</KanbanBoard>
	);
};
